import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react'
import { useStore } from 'jotai'

import { userInputAtom } from '@/atoms/chatAtoms'
import {
  ChatComposerHasDraftContext,
  ChatInputScopeContext,
  ChatInputSetContext,
  ChatInputValueContext
} from '@/context/chat'

type AttachmentKeyResolver = (chatId?: string | null) => string

export const getDraftAttachmentScopeId = (inputScopeId: string) =>
  `draft:${inputScopeId}`

/**
 * Setter-only subscription. Use this from ChatUI / handlers so typing does not
 * re-render the message list on every keystroke.
 */
export function useScopedSetUserInput(): Dispatch<SetStateAction<string>> {
  const scopedSetUserInput = useContext(ChatInputSetContext)
  const inputScope = useContext(ChatInputScopeContext)
  const atomStore = useStore()

  const setAtomInput = useCallback<Dispatch<SetStateAction<string>>>(
    nextInput => {
      atomStore.set(userInputAtom, nextInput)
    },
    [atomStore]
  )

  if (inputScope.hasScopedInputState) {
    return scopedSetUserInput
  }

  return setAtomInput
}

/** Full draft string — only the composer / mention helpers should use this. */
export function useScopedUserInputValue(): string {
  const scopedUserInput = useContext(ChatInputValueContext)
  const inputScope = useContext(ChatInputScopeContext)
  const atomStore = useStore()
  const [atomInput, setAtomInputState] = useState(() => atomStore.get(userInputAtom))

  useEffect(() => {
    if (inputScope.hasScopedInputState) {
      return
    }

    setAtomInputState(atomStore.get(userInputAtom))

    return atomStore.sub(userInputAtom, () => {
      setAtomInputState(atomStore.get(userInputAtom))
    })
  }, [atomStore, inputScope.hasScopedInputState])

  if (inputScope.hasScopedInputState) {
    return scopedUserInput
  }

  return atomInput
}

/**
 * Empty ↔ non-empty only. Safe for ChatUI suggestion gating without per-keystroke
 * re-renders once the draft already has content.
 */
export function useHasComposerDraft(): boolean {
  const scopedHasDraft = useContext(ChatComposerHasDraftContext)
  const inputScope = useContext(ChatInputScopeContext)
  const atomStore = useStore()
  const [atomHasDraft, setAtomHasDraft] = useState(
    () => atomStore.get(userInputAtom).trim().length > 0
  )

  useEffect(() => {
    if (inputScope.hasScopedInputState) {
      return
    }

    const sync = () => {
      const next = atomStore.get(userInputAtom).trim().length > 0
      setAtomHasDraft(previous => (previous === next ? previous : next))
    }

    sync()
    return atomStore.sub(userInputAtom, sync)
  }, [atomStore, inputScope.hasScopedInputState])

  if (inputScope.hasScopedInputState) {
    return scopedHasDraft
  }

  return atomHasDraft
}

export function useScopedUserInput(): [
  string,
  Dispatch<SetStateAction<string>>
] {
  return [useScopedUserInputValue(), useScopedSetUserInput()]
}

export function useScopedAttachmentKey({
  activeAttachmentKey,
  chatId,
  getAttachmentStoreKey
}: {
  activeAttachmentKey: string
  chatId?: string | null
  getAttachmentStoreKey: AttachmentKeyResolver
}) {
  // Scope only — never ChatInputStateContext. That legacy value includes the
  // draft string and would re-subscribe the message list on every keystroke.
  const { hasScopedInputState, inputScopeId } = useContext(ChatInputScopeContext)
  const trimmedChatId = typeof chatId === 'string' ? chatId.trim() : ''

  if (trimmedChatId) {
    return getAttachmentStoreKey(trimmedChatId)
  }

  if (hasScopedInputState) {
    return getAttachmentStoreKey(getDraftAttachmentScopeId(inputScopeId))
  }

  return activeAttachmentKey
}
